import { createRouter, createWebHistory, RouteLocationNormalized, RouteRecordRaw } from 'vue-router'
import { useEquipmentStore } from '@/store/equipment'
import { useSessionStore } from '@/store/session'

// Extend Vue Router's RouteMeta with fallback route support
declare module 'vue-router' {
    interface RouteMeta {
        fallbackRoute?: string | ((route: RouteLocationNormalized) => { name: string; params?: Record<string, string> })
    }
}

// AUTH
import loginPage from '@/components/auth/login-page.vue'
import unifiedLogin from '@/components/auth/unified-login.vue'
import deviceLogin from '@/components/auth/device-login.vue'
import partnerLogin from '@/components/auth/partner-login.vue'
import forgottenPassword from '@/components/auth/password-reset-request.vue'
import passwordReset from '@/components/auth/password-reset.vue'
import logoff from '@/components/log-off.vue'

// DEVICE
import equipmentPage from '@/components/equipment/equipment-page.vue'
import equipmentSpareParts from '@/components/equipment/equipment-spareparts.vue'
import equipmentConf from '@/components/equipment/equipment-conf.vue'
import equipmentDocuments from '@/components/equipment/equipment-documents.vue'
import equipmentData from '@/components/equipment/equipment-data.vue'
import equipmentDeviations from '@/components/equipment/equipment-deviations.vue'
import equipmentSoftware from '@/components/equipment/equipment-software.vue'

// MATERIAL DETAILS VIEW
import materialView from '@/components/material-view.vue'

// SPARE PARTS
import sparepartView from '@/components/spareparts/sparepart-view.vue'

// PARTNER
import partnerView from '@/components/partner/partner-view.vue'
import sparePartSearch from '@/components/partner/spare-parts/search/partner-spare-part-search.vue'
//import sparePartOverview from '@/components/partner/partner-spare-part-overview.vue'
import partnerDevices from '@/components/partner/partner-devices.vue'
import userAccount from '@/components/partner/user-account.vue'
import modelSpareparts from '@/components/partner/spare-parts/model-spareparts.vue'
import allSparepartsCatalog from '@/components/partner/spare-parts/all-spareparts-catalog.vue'
import contactSupport from '@/components/partner/contact-support.vue'
//import tempTest from '@/components/temp-test.vue'

// SHOPPING CART
import shoppingCart from '@/components/shopping-cart/shopping-cart.vue'
import shoppingCartContent from '@/components/shopping-cart/shopping-cart-content.vue'
import shoppingCartCheckout from '@/components/shopping-cart/shopping-cart-checkout.vue'

// ADMIN
import adminView from '@/components/admin/admin-view.vue'
import adminUserList from '@/components/admin/admin-user-list.vue'
import adminPartnerList from '@/components/admin/admin-partner-list.vue'
import equipmentTest from '@/components/admin/equipment-test.vue'
import adminEquipmentList from '@/components/admin/admin-equipment-list.vue'
import adminSparePartOverview from '@/components/admin/admin-spare-part-overview.vue'
import adminSyncEventsList from '@/components/admin/admin-sync-event-list.vue'
import adminEquipmentWarnings from '@/components/admin/admin-equipment-warnings.vue'
import adminOrderProposalList from '@/components/admin/admin-order-proposal-list.vue'

import { DEBUGC } from '@/util'

const routes: RouteRecordRaw[] = [
    // ****** AUTHENTICATION PAGES ******
    {
        path: '/sn/:sn',
        redirect: to => {
            const { params } = to
            return { name: 'device-login', query: { id: params.sn } }
        },
        meta: { unprotected: true },
    },
    {
        path: '/sn',
        redirect: to => {
            return { name: 'device-login', query: { id: to.query.id } }
        },
        meta: { unprotected: true },
    },
    {
        path: '/auth',
        component: loginPage,
        meta: { unprotected: true },
        redirect: { name: 'combined-login' },
        children: [
            {
                name: 'combined-login',
                path: '',
                component: unifiedLogin,
                meta: { unprotected: true },
            },
            {
                name: 'device-login',
                path: 'sn',
                component: deviceLogin,
                meta: { unprotected: true },
            },
            {
                name: 'partner-login',
                path: 'partner',
                component: partnerLogin,
                meta: { unprotected: true }
            },
            {
                name: 'forgotten-password',
                path: 'forgottenPassword',
                component: forgottenPassword,
                meta: { unprotected: true }
            },
            {
                name: 'password-reset',
                path: 'passwordReset',
                component: passwordReset,
                meta: { unprotected: true }
            }
        ]
    },
    {
        name: 'logoff',
        path: '/logoff',
        component: logoff,
    },
    // ****** EQUIPMENT ******
    {
        path: '/equipment/:sn',
        component: equipmentPage,
        name: 'equipment',
        redirect: { name: 'equipment-spareparts' },
        meta: { fallbackRoute: 'partner-sparepart-search' },
        children: [
            {
                name: 'equipment-spareparts',
                path: 'spareparts',
                component: equipmentSpareParts
            },
            {
                name: 'equipment-conf',
                path: 'conf',
                component: equipmentConf
            },
            {
                name: 'equipment-documents',
                path: 'documents',
                component: equipmentDocuments
            },
            {
                name: 'equipment-software',
                path: 'software',
                component: equipmentSoftware
            },
            {
                name: 'equipment-data',
                path: 'data',
                component: equipmentData
            },
            {
                name: 'equipment-deviations',
                path: 'deviations',
                component: equipmentDeviations
            }
        ]
    },
    {
        path: '/equipment/:sn/:matno',
        component: materialView,
        name: 'eq-material',
        meta: { fallbackRoute: (route: RouteLocationNormalized) => ({ name: 'equipment-spareparts', params: { sn: route.params.sn as string } }) }
    },

    // ****** MATERIAL ******
    {
        path: '/material/:matno',
        component: materialView,
        name: 'material',
        meta: { fallbackRoute: 'partner-sparepart-search' }
    },

    // ****** SPARE PART OVERVIEW ******
    {
        path: '/spareparts/:model',
        component: modelSpareparts,
        name: 'sparepartOverview',
        meta: { fallbackRoute: 'partner-sparepart-search' }
    },
    {
        path: '/spareparts-catalog',
        component: allSparepartsCatalog,
        name: 'sparepartCatalog',
        meta: { fallbackRoute: 'partner-sparepart-search' }
    },

    // ****** PARTNER ******
    {
        path: '/partner',
        component: partnerView,
        name: 'partner',
        redirect: { name: 'partner-sparepart-search' },
        meta: { partnerOnly: true },
        children: [
            {
                name: 'partner-sparepart-search',
                path: 'find-spareparts',
                component: sparePartSearch
            },
            //{
            //    name: 'partner-spareparts',
            //    path: 'spareparts',
            //    component: sparePartOverview
            //},
            {
                name: 'partner-spareparts',
                path: 'spareparts',
                redirect: { name: 'partner-sparepart-search' }
            },
            {
                name: 'partner-devices',
                path: 'devices',
                component: partnerDevices
            },
            {
                name: 'partner-account',
                path: 'account',
                component: userAccount
            },
            {
                name: 'partner-user-list',
                path: 'users',
                component: userAccount
            },
            {
                name: 'partner-contact-support',
                path: 'contact-support',
                component: contactSupport
            },
            {
                name: 'test',
                path: 'test',
                component: sparepartView
            }
        ]
    },

    // ****** SHOPPING CART ******
    {
        path: '/cart',
        component: shoppingCart,
        name: 'shopping-cart',
        redirect: { name: 'shopping-cart-content' },
        meta: { partnerOnly: true, fallbackRoute: 'partner-sparepart-search' },
        children: [
            {
                name: 'shopping-cart-content',
                path: 'content',
                component: shoppingCartContent
            },
            {
                name: 'shopping-cart-checkout',
                path: 'checkout',
                component: shoppingCartCheckout
            }
        ]
    },

    // ****** ADMIN ******
    {
        path: '/admin',
        component: adminView,
        name: 'admin',
        redirect: { name: 'admin-equipment-list' },
        meta: { partnerOnly: true, fallbackRoute: 'partner-sparepart-search' },
        children: [
            {
                name: 'admin-user-list',
                path: 'users',
                component: adminUserList
            },
            {
                name: 'admin-partner-list',
                path: 'partners',
                component: adminPartnerList
            },
            {
                name: 'admin-equipment-test',
                path: 'equipment',
                component: equipmentTest
            },
            {
                name: 'admin-equipment-list',
                path: 'equipments',
                component: adminEquipmentList
            },
            {
                name: 'admin-spare-parts',
                path: 'spareparts',
                component: adminSparePartOverview
            },
            {
                name: 'admin-sync-events-list',
                path: 'sync',
                component: adminSyncEventsList
            },
            {
                name: 'admin-order-proposals',
                path: 'order-proposals',
                component: adminOrderProposalList
            }
        ]
    },

    // ****** MISC ******
    {
        name: 'admin-equipment-warnings',
        path: '/warnings/:eqNo',
        component: adminEquipmentWarnings,
        meta: { fallbackRoute: 'admin-equipment-list' }
    },

    // ****** FAILOVER ******
    {
        path: '/',
        redirect: '/auth'
    },
    {
        path: '/:catchAll(.*)',
        redirect: '/auth'
    },
];

const router = createRouter({
    history: createWebHistory(), 
    routes    
})

// Navigation guard for authentication
router.beforeEach(async (to, from, next) => {
    const sessionStore = useSessionStore()

    DEBUGC(`ROUTER guard: '${from.fullPath}' -> '${to.fullPath}'`, 'limegreen')
    const requiresAuth = !(to.meta.unprotected === true);

    // Authentication requirement
    if (requiresAuth) {
        const isAuthenticated = await sessionStore.isAuthenticated()

        if (!isAuthenticated) {
            const returnPath = encodeURIComponent(to.fullPath)
            const isPartnerRoute = to.matched.some(r => r.meta.partnerOnly)

            if (isPartnerRoute) {
                next(`/auth/partner?return=${returnPath}`)
            } else {
                next(`/auth?return=${returnPath}`)
            }
            return;
        }
    }

    // Maintain "Current equipment S/N"
    const equipmentStore = useEquipmentStore()
    const equipmentRoute = to.matched.find(r => r.name == 'equipment')

    if (equipmentRoute) {
        equipmentStore.setActiveSerialNo(to.params.sn as string)
    }
    else if (to.name == 'material') {
        if (equipmentStore.activeSerialNo.value) {
            // redirect
            next({ name: 'eq-material', params: { sn: equipmentStore.activeSerialNo.value, matno: to.params.matno } })
            return
        }
    }
    else {
        // Route.to != 'material'
        equipmentStore.setActiveSerialNo(null)
    }

    next();
})

export default router;

